import copy from 'copy-to-clipboard'

import { CommandBarItem } from '@sketch/modules-common'
import { ElementType, SketchElement, generateLayerCSS } from '@sketch/inspector'

import { ReactComponent as ExportIcon } from '@sketch/icons/img-arrow-down-circle-17'
import { ReactComponent as OpenFrameIcon } from '@sketch/icons/frame-17'
import { ReactComponent as CopyCSSIcon } from '@sketch/icons/img-markdown-code-inline-17'
import { ReactComponent as CopyLinkIcon } from '@sketch/icons/markdown-link-share-17'

interface CreateInspectCommandsProps {
  navigateToFrame: (frameUUID: string, source: 'CTA') => void
  openExportModal: (id: bigint, name: string) => void
  selectedElement: SketchElement | null
  showToast: (message: string) => void
  sketchSceneRootElement: SketchElement | null
  createLayerLink: (layerPathHash: string) => string | null
  shouldEnableCopyLink: boolean
}

export const createInspectCommands = ({
  navigateToFrame,
  openExportModal,
  selectedElement,
  showToast,
  sketchSceneRootElement,
  createLayerLink,
  shouldEnableCopyLink,
}: CreateInspectCommandsProps): CommandBarItem[] => {
  if (!selectedElement) {
    return []
  }

  const nearestOpenableFrame = selectedElement.nearestOpenableFrame
  const isOpenableFrameCurrentlyOpen =
    nearestOpenableFrame &&
    nearestOpenableFrame.targetUUID === sketchSceneRootElement?.elementUUID

  const openFrameCommand: CommandBarItem | null =
    nearestOpenableFrame?.type === ElementType.Artboard &&
    !isOpenableFrameCurrentlyOpen
      ? {
          id: `inspect.open-frame.${selectedElement.identifier}`,
          analyticsId: 'inspect.open-frame',
          kind: 'command',
          title: nearestOpenableFrame.isSelf
            ? 'Open Frame'
            : 'Open Top-Level Frame',
          subtitle: selectedElement.name,
          section: 'Inspect',
          keywords: ['inspect', 'open', 'frame', 'navigate'],
          icon: OpenFrameIcon,
          priority: 25,
          run: () => navigateToFrame(nearestOpenableFrame.targetUUID, 'CTA'),
        }
      : null

  const copyLinkCommand: CommandBarItem | null = shouldEnableCopyLink
    ? {
        id: `inspect.copy.link.${selectedElement.identifier}`,
        analyticsId: 'inspect.copy.link',
        kind: 'command',
        title: 'Copy Link',
        subtitle: `Copy a link to "${selectedElement.name}"`,
        section: 'Inspect',
        keywords: ['inspect', 'copy', 'link', 'share'],
        icon: CopyLinkIcon,
        priority: 25,
        run: () => {
          const url = createLayerLink(selectedElement.layerPathHash)
          if (url) {
            copy(url)
            showToast('Link copied')
          }
        },
      }
    : null

  return [
    ...(openFrameCommand ? [openFrameCommand] : []),
    ...(copyLinkCommand ? [copyLinkCommand] : []),
    {
      id: `inspect.copy-css.${selectedElement.identifier}`,
      analyticsId: 'inspect.copy-css',
      kind: 'command',
      title: 'Copy CSS',
      subtitle: `Copy CSS attributes for "${selectedElement.name}"`,
      section: 'Inspect',
      keywords: ['inspect', 'export', 'css'],
      icon: CopyCSSIcon,
      priority: 25,
      run: () => {
        const layerCSS = generateLayerCSS(selectedElement)

        if (!layerCSS.css.length) return
        copy(layerCSS.toString())
        showToast('CSS copied')
      },
    },
    {
      id: `inspect.export.${selectedElement.identifier}`,
      analyticsId: 'inspect.export',
      kind: 'command',
      title: 'Export as…',
      subtitle: `Export "${selectedElement.name}" as an image`,
      section: 'Inspect',
      keywords: ['inspect', 'export', 'download', 'asset'],
      icon: ExportIcon,
      priority: 25,
      run: () =>
        openExportModal(selectedElement.identifier, selectedElement.name),
    },
  ]
}
